import React, { useEffect, useRef, useState } from 'react'
import { useMutation, useApolloClient } from '@apollo/client'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { dispatchSetUser } from '../context/AuthContext'
import { dispatchToast } from '../context/ToastContext'
import UpdateBannerModal from './UpdateBannerModal'
import UpdateAvatarModal from './UpdateAvatarModal'
import { atualizarBannerUrl, atualizarImagemUsuario } from '../services/userService'
import { ATLETA_POR_ID, AtletaData, AtletaVars } from '../services/atletaService'
import {
    CRIAR_SEGUIDOR_MUTATION,
    DELETAR_SEGUIDOR_MUTATION,
    CriarSeguidorResponse,
    buscarSeguidorAtleta,
} from '../services/seguidorService'

interface ProfilecardThreeProps {
    usuario: {
        nome: string
        imagemUrl: string
        bannerUrl?: string | null
        email?: string
        username?: string
    }
    atletaId: string
    isAtletaLogado: boolean
    isAdmin?: boolean
}

const ProfilecardThree: React.FC<ProfilecardThreeProps> = ({ usuario, atletaId, isAtletaLogado, isAdmin }) => {
    const auth = useAuth()
    const client = useApolloClient()
    const userObj = (auth?.user as any) || (typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('user') || 'null') : null)
    const seguidorUserId = userObj?.atleta?.id ?? ''
    const navigate = useNavigate()

    const [seguir] = useMutation<{ criarSeguidor: CriarSeguidorResponse }, { input: any }>(CRIAR_SEGUIDOR_MUTATION)
    const [deletar] = useMutation(DELETAR_SEGUIDOR_MUTATION)
    const [followId, setFollowId] = useState<string | null>(null)
    const [isFollowing, setIsFollowing] = useState(false)
    const [loading, setLoading] = useState(false)
    const [erro, setErro] = useState('')
    const [bannerUrl, setBannerUrl] = useState<string>(usuario.bannerUrl || '../assets/images/comingsoon.svg')
    const [showBannerModal, setShowBannerModal] = useState(false)
    const [showAvatarModal, setShowAvatarModal] = useState(false)
    const [avatarUrl, setAvatarUrl] = useState(usuario.imagemUrl)
    const [menuOpen, setMenuOpen] = useState(false)
    const menuRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const fetchFollow = async () => {
            if (!seguidorUserId || !atletaId) return
            try {
                const data = await buscarSeguidorAtleta(seguidorUserId, atletaId)
                const id = data?.id ?? null
                setFollowId(id)
                setIsFollowing(!!id)
            } catch (e) {
                console.error(e)
            }
        }
        fetchFollow()
    }, [seguidorUserId, atletaId])

    useEffect(() => {
        setBannerUrl(usuario.bannerUrl || '../assets/images/comingsoon.svg')
    }, [usuario.bannerUrl])

    const handleClick = async () => {
        if (loading || !seguidorUserId || !atletaId) return
        setErro('')
        setLoading(true)
        try {
            if (isFollowing && followId) {
                await deletar({ variables: { id: followId } })
                setIsFollowing(false)
                setFollowId(null)
            } else {
                const { data } = await seguir({
                    variables: {
                        input: {
                            seguidorId: seguidorUserId,
                            seguidoAtletaId: atletaId,
                            seguidoTorneioId: null,
                        },
                    },
                })
                const id = data?.criarSeguidor.id
                if (id) setFollowId(id)
                setIsFollowing(true)
            }
        } catch (e) {
            setErro('Não foi possível atualizar. Tente novamente.')
        } finally {
            setLoading(false)
        }
    }

    const toggleMenu = () => setMenuOpen((v) => !v)

       useEffect(() => {
        if (!menuOpen) return
        const handleClickOutside = (e: MouseEvent) => {
            const dropdown = document.getElementById('dropdownMenu4')
            if (
                menuRef.current &&
                !menuRef.current.contains(e.target as Node) &&
                dropdown &&
                !dropdown.contains(e.target as Node)
            ) {
                setMenuOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [menuOpen])

    const updateAtletaCache = (field: 'imagemUrl' | 'bannerUrl', value: string) => {
        try {
            const cached = client.readQuery<AtletaData, AtletaVars>({
                query: ATLETA_POR_ID,
                variables: { id: atletaId },
            })
            if (cached?.atletaPorId) {
                client.writeQuery<AtletaData, AtletaVars>({
                    query: ATLETA_POR_ID,
                    variables: { id: atletaId },
                    data: {
                        atletaPorId: {
                            ...cached.atletaPorId,
                            usuario: {
                                ...cached.atletaPorId.usuario,
                                [field]: value,
                            },
                        },
                    },
                })
            }
        } catch {
            // Cache miss — nenhuma ação necessária
        }
    }

    const handleSaveBanner = async (file: File) => {
        if (!userObj?.id) return
        try {
            const url = await atualizarBannerUrl(userObj.id, file)
            setBannerUrl(url)
            const updated = { ...userObj, bannerUrl: url }
            localStorage.setItem('user', JSON.stringify(updated))
            dispatchSetUser(updated)
            updateAtletaCache('bannerUrl', url)
            dispatchToast('Banner atualizado!', 'success')
        } catch (err) {
            dispatchToast('Erro ao atualizar banner.', 'error')
        }
    }

    const handleSaveAvatar = async (file: File) => {
        if (!userObj?.id) return
        try {
            const url = await atualizarImagemUsuario(userObj.id, file)
            setAvatarUrl(url)
            const updated = { ...userObj, imagemUrl: url }
            localStorage.setItem('user', JSON.stringify(updated))
            dispatchSetUser(updated)
            updateAtletaCache('imagemUrl', url)
            dispatchToast('Avatar atualizado!', 'success')
        } catch (err) {
            dispatchToast('Erro ao atualizar avatar.', 'error')
        }
    }

    return (
        <>
            <div className="card w-100 border-0 p-0 bg-white shadow-xss rounded-xxl">
                <div
                    id='banner-usuario'
                    className="card-body h250 p-0 rounded-xxl overflow-hidden m-3"
                    style={{ height: '300px', backgroundImage: `url("${bannerUrl}")`, backgroundSize: 'cover', backgroundPosition: 'center' }}
                ></div>
                <div className="card-body p-0 position-relative">
                    <figure id='avatar-usuario' className="avatar position-absolute w100 z-index-1" style={{ top: '-40px', left: '30px' }}>
                        <img
                            src={avatarUrl || '../assets/images/avatar.jpg'}
                            alt={usuario.nome}
                            className="float-right p-1 bg-white rounded-circle w-100"
                        />
                    </figure>
                    <h4 id='nome-usuario' className="fw-700 font-sm mt-2 mb-lg-5 mb-4 pl-15">
                        {usuario.nome}
                        {usuario.email && (
                            <span className="fw-500 font-xssss text-grey-500 mt-1 mb-3 d-block">
                                {usuario.username ? `@${usuario.username}` : usuario.email}
                            </span>
                        )}
                    </h4>
                    <div className="d-flex align-items-center justify-content-center position-absolute-md right-15 top-0 me-2">
                        {!isAtletaLogado && !isAdmin && (
                            <button
                                onClick={handleClick}
                                disabled={loading}
                                className={`d-none d-lg-block p-3 z-index-1 rounded-3 font-xsssss text-uppercase fw-700 ls-3 btn btn-primary`}
                            >
                                {loading
                                    ? 'Aguarde...'
                                    : isFollowing
                                    ? 'SEGUINDO'
                                    : 'SEGUIR'}
                            </button>
                        )}
                        {isAtletaLogado && (
                            <>
                                <div
                                    id="dropdownMenu4"
                                    className="d-none d-lg-block bg-greylight btn-round-lg ms-2 rounded-3 text-grey-700"
                                    onClick={toggleMenu}
                                    data-toggle="dropdown" aria-haspopup="true" aria-expanded="false"
                                >
                                    <i className="ti-more font-md text-dark"></i>
                                </div>
                                <div
                                    ref={menuRef}
                                    className={`dropdown-menu dropdown-menu-end p-4 rounded-xxl border-0 shadow-lg ${menuOpen ? 'show' : ''}`}
                                    aria-labelledby="dropdownMenu4"
                                >
                                    <button className="dropdown-item d-flex align-items-center" onClick={() => { setShowAvatarModal(true); setMenuOpen(false); }}>
                                        <i className="bi bi-person-circle me-3 font-lg"></i>
                                        <span className="fw-600 text-grey-900 font-xssss">Alterar Avatar</span>
                                    </button>
                                    <button className="dropdown-item d-flex align-items-center" onClick={() => { setShowBannerModal(true); setMenuOpen(false); }}>
                                        <i className="bi bi-camera me-3 font-lg"></i>
                                        <span className="fw-600 text-grey-900 font-xssss">Alterar Banner</span>
                                    </button>
                                    <button className="dropdown-item d-flex align-items-center" onClick={() => { navigate('/accountinformation'); setMenuOpen(false); }}>
                                        <i className="bi bi-pencil me-3 font-lg"></i>
                                        <span className="fw-600 text-grey-900 font-xssss">Editar Perfil</span>
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                    {erro && (
                        <div className="alert alert-danger mt-2" role="alert">
                            {erro}
                        </div>
                    )}
                </div>
            </div>
            <UpdateBannerModal
                open={showBannerModal}
                onClose={() => setShowBannerModal(false)}
                currentImage={bannerUrl}
                onSave={handleSaveBanner}
            />
            <UpdateAvatarModal
                open={showAvatarModal}
                onClose={() => setShowAvatarModal(false)}
                currentImage={avatarUrl || undefined}
                onSave={handleSaveAvatar}
            />
        </>
    )
}

export default ProfilecardThree
